'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2, MapPin, Check, AlertCircle } from 'lucide-react'
import { AddressAutocomplete, type SelectedAddress } from '@/components/shared/AddressAutocomplete'
import { formatDropoffLine, etaMinutes } from '@/lib/delivery/formatDropoff'
import {
  DELIVERY_ERROR_UX,
  type DeliveryErrorKind,
} from './deliveryErrorUx'
import type { DropoffAddress, HeldQuote } from '../types'
import { requestDeliveryQuote } from './requestDeliveryQuote'
import { WhyFeeLink } from '../WhyFeeLink'

/**
 * Paso de dirección del checkout de delivery.
 *
 * ── QUÉ SE MUESTRA ─────────────────────────────────────────────────────────
 * Esta pantalla responde dos preguntas: ¿llegamos a tu zona? y ¿cuánto cuesta?
 * El precio del envío SÍ se muestra aquí, junto al ETA (decisión 2026-09-03: el
 * panel de admin promete "ve el costo del envío antes de pagar", y el cliente lo
 * iba a ver igual dos pasos después, en peor momento). Sigue apareciendo como
 * línea "Envío" en los totales. Lo que NO cambia: el número es VERBATIM de la
 * cotización — nunca se recalcula ni se deriva de la distancia.
 *
 * ── DE DÓNDE SALE EL DINERO ─────────────────────────────────────────────────
 * `onQuote` entrega el quote TAL CUAL lo devolvió /api/delivery/quote. Ese
 * `feeAmountCents` es la única verdad del envío en todo el checkout: nunca se
 * recalcula, no se re-deriva de la distancia, y editar el apto NO re-cotiza (el
 * apto no viaja al cotizador). Si el cliente cambia la dirección, el quote se
 * INVALIDA (`onClearQuote`) y hay que cotizar de nuevo — un fee que no
 * corresponda a lo cotizado revienta con 422 DESPUÉS del cobro.
 *
 * ── POR QUÉ SE EXIGE ELEGIR UNA SUGERENCIA ──────────────────────────────────
 * El chequeo de zona necesita lat/lng; sin coordenadas falla ABIERTO (ver
 * AddressAutocomplete) y el "no llegamos a tu zona" se vuelve decorativo. Así
 * que sólo cotizamos al elegir una sugerencia de Google. Si Google no carga, el
 * paso no habilita el avance y ofrece la salida a pickup — cerrado del lado
 * seguro, con el pedido para recoger siempre disponible.
 */

type QuoteState = 'idle' | 'loading' | 'ok' | 'out_of_range' | 'error'

/**
 * Tipo de fallo del cotizador. Nunca se le muestra el error crudo al cliente.
 * La POLÍTICA de qué puede hacer ante cada uno (reintentar / cambiar dirección /
 * pickup) vive en `deliveryErrorUx.ts`, que sí se testea.
 */
type ErrorKind = DeliveryErrorKind

type Props = {
  locationId: string
  accentColor: string
  /** Dirección ya confirmada (vive en el padre: sobrevive al cierre del sheet). */
  dropoff: DropoffAddress | null
  /** Cotización vigente. Si llega junto con `dropoff`, el paso arranca en éxito sin re-cotizar. */
  heldQuote: HeldQuote | null
  /** Cotización OK: el padre congela quote + dirección. El quote se pasa VERBATIM. */
  onQuote: (quote: HeldQuote, dropoff: DropoffAddress) => void
  /** La dirección dejó de ser válida (tecleo, fuera de zona, error): el padre borra el quote. */
  onClearQuote: () => void
  /** "Mejor recojo yo": el padre vuelve a pickup y limpia dirección + quote. */
  onSwitchToPickup: () => void
  onContinue: () => void
  onBack: () => void
  /** Texto del botón principal. El mini-sheet fuera del checkout pasa "Listo"; dentro del checkout no se pasa y queda "Siguiente →". */
  continueLabel?: string
  /** Abre el explicador "¿Por qué cobramos envío?" (spec 2026-09-03). Sin él no hay enlace. */
  onWhyFee?: () => void
}

export function AddressStep({
  locationId,
  accentColor,
  dropoff,
  heldQuote,
  onQuote,
  onClearQuote,
  onSwitchToPickup,
  onContinue,
  onBack,
  continueLabel,
  onWhyFee,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tCommon = useTranslations('common')

  // Texto del input. Al volver al paso se rehidrata de la dirección ya elegida.
  const [text, setText] = useState(dropoff ? formatDropoffLine(dropoff) : '')
  const [unit, setUnit] = useState(dropoff?.unit ?? '')
  // Dirección parseada de la última sugerencia elegida (sin el apto).
  const [selected, setSelected] = useState<SelectedAddress | null>(
    dropoff ? { street: dropoff.street, city: dropoff.city, state: dropoff.state, zip: dropoff.zip, lat: dropoff.lat, lng: dropoff.lng } : null,
  )
  const [state, setState] = useState<QuoteState>(dropoff && heldQuote ? 'ok' : 'idle')
  const [errorKind, setErrorKind] = useState<ErrorKind | null>(null)
  // Secuencia de cotizaciones. En un REF (no state) a propósito: la comparación
  // ocurre DESPUÉS del await, y un valor capturado en la clausura del render
  // siempre se compararía consigo mismo. Descarta respuestas viejas: si el
  // cliente elige otra dirección mientras una está en vuelo, la que llega tarde
  // NO puede pisar a la nueva — eso dejaría un fee cotizado para una dirección
  // distinta a la que se envía.
  const reqRef = useRef(0)

  // Montado/desmontado. `reqRef` descarta respuestas SUPERADAS; esto descarta
  // las que llegan cuando el paso ya no existe (el mini-sheet se cerró, o el
  // cliente tocó "Recojo yo"). Sin esto, un `onQuote` tardío volvería a poner
  // modo domicilio en el padre (spec 2026-09-03 §4.3).
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  const eta = etaMinutes(heldQuote?.etaDropoffSeconds)

  async function quote(addr: SelectedAddress) {
    const myReq = ++reqRef.current
    setSelected(addr)
    setState('loading')
    setErrorKind(null)
    // La dirección cambió ⇒ el quote anterior ya no aplica. Se invalida ANTES de
    // pedir el nuevo para que no exista un instante con dirección A y fee de B.
    onClearQuote()

    const result = await requestDeliveryQuote(locationId, { ...addr, ...(unit.trim() ? { unit: unit.trim() } : {}) })

    // Respuesta de una cotización superada por otra más nueva: se ignora.
    if (!mountedRef.current || myReq !== reqRef.current) return

    if (result.kind === 'error') {
      setState('error')
      setErrorKind(result.errorKind)
      return
    }
    if (result.kind === 'out_of_range') {
      setState('out_of_range')
      return
    }
    setState('ok')
    onQuote(result.quote, { ...addr, ...(unit.trim() ? { unit: unit.trim() } : {}) })
  }

  function handleText(v: string) {
    setText(v)
    // Tecleo libre después de haber elegido: la dirección ya no coincide con lo
    // cotizado, así que el quote muere y hay que volver a elegir una sugerencia.
    if (state !== 'idle') {
      setState('idle')
      setSelected(null)
      setErrorKind(null)
      reqRef.current += 1   // invalida cualquier respuesta en vuelo
      onClearQuote()
    }
  }

  function handleUnit(v: string) {
    setUnit(v)
    // El apto NO viaja al cotizador (no cambia zona ni precio): se propaga al
    // padre reusando el MISMO quote congelado. Nada se recalcula.
    if (state === 'ok' && heldQuote && selected) {
      onQuote(heldQuote, { ...selected, ...(v.trim() ? { unit: v.trim() } : {}) })
    }
  }

  // Título + cuerpo por tipo de fallo. Cada uno nombra lo que pasó en términos
  // del cliente ("no llegamos ahí", "se cayó tu conexión") — nunca el código HTTP
  // ni un "error inesperado".
  const errorCopy: Record<ErrorKind, { title: string; body: string }> = {
    not_available: { title: t('deliveryUnavailableTitle'), body: t('deliveryUnavailableHere') },
    rate_limited: { title: t('deliveryTooManyTriesTitle'), body: t('deliveryTooManyTries') },
    network: { title: t('deliveryNetworkErrorTitle'), body: t('deliveryNetworkError') },
    quote_failed: { title: t('deliveryQuoteFailedTitle'), body: t('deliveryQuoteFailed') },
  }
  const errorUx = errorKind ? DELIVERY_ERROR_UX[errorKind] : null
  const errorWarn = errorUx?.tone === 'warn'

  const canContinue = state === 'ok' && !!heldQuote

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← {t('back')}
        </button>
        <h2 className="flex-1 text-center text-xl font-bold text-gray-900 -ml-12">
          {t('addressTitle')}
        </h2>
      </div>

      {/* Scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <p className="text-sm text-gray-500">{t('addressHelp')}</p>

        <div className="mt-4">
          {/* Sin htmlFor: el <input> lo renderiza AddressAutocomplete y no expone id. */}
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t('addressStreetLabel')}
          </label>
          <AddressAutocomplete
            value={text}
            onChange={handleText}
            onPlaceSelect={(p) => {
              setText(formatDropoffLine(p))
              void quote(p)
            }}
            placeholder={t('addressStreetPlaceholder')}
            className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
          <p className="mt-1 text-xs text-gray-500">{t('addressPickSuggestion')}</p>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="delivery-unit">
            {t('addressUnitLabel')}
          </label>
          <input
            id="delivery-unit"
            type="text"
            value={unit}
            onChange={(e) => handleUnit(e.target.value)}
            placeholder={t('addressUnitPlaceholder')}
            maxLength={120}
            autoComplete="address-line2"
            className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
          <p className="mt-1 text-xs text-gray-500">{t('addressUnitHelp')}</p>
        </div>

        {/* Cotizando */}
        {state === 'loading' && (
          <div className="mt-4 flex items-center gap-2 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
            <span className="text-sm text-gray-600">{t('addressChecking')}</span>
          </div>
        )}

        {/* Sí llegamos — con el precio DICHO en la misma línea que el ETA, en voz baja:
            se dice, no se grita (spec 2026-09-03 §4.4, tono ajustado por Samuel viendo el preview). */}
        {state === 'ok' && (
          <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-green-900">{t('addressInZone')}</p>
                {(eta != null || heldQuote) && (
                  <p className="text-xs text-green-800 mt-0.5">
                    {eta != null && t('addressEta', { minutes: eta })}
                    {eta != null && heldQuote && ' · '}
                    {heldQuote && t('addressInZoneFee', { fee: `$${(heldQuote.feeAmountCents / 100).toFixed(2)}` })}
                    {heldQuote && onWhyFee && (
                      <>
                        {' · '}
                        <WhyFeeLink onClick={onWhyFee} className="text-green-800" />
                      </>
                    )}
                  </p>
                )}
                {selected && (
                  <p className="text-xs text-green-800/80 mt-1 break-words">
                    {formatDropoffLine(selected)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fuera de zona */}
        {state === 'out_of_range' && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-sm font-bold text-amber-900">{t('addressOutOfRangeTitle')}</p>
                <p className="text-xs text-amber-800 mt-0.5">{t('addressOutOfRangeBody')}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => handleText('')}
                className="rounded-full px-4 py-2 text-xs font-bold text-white transition hover:opacity-95"
                style={{ backgroundColor: accentColor }}
              >
                {t('addressChangeAddress')}
              </button>
              <button
                type="button"
                onClick={onSwitchToPickup}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
              >
                {t('addressSwitchToPickup')}
              </button>
            </div>
          </div>
        )}

        {/* 409 / 429 / 502 / red caída — cada uno con su salida REAL (ver ERROR_UX).
            Ámbar = cambió el plan (esto no se va a arreglar solo); gris = transitorio,
            reintentar sirve. El pickup está en los cuatro. */}
        {state === 'error' && errorKind && errorUx && (
          <div
            className={`mt-4 rounded-2xl border px-4 py-3 ${
              errorWarn ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${errorWarn ? 'text-amber-600' : 'text-gray-500'}`}
              />
              <div className="min-w-0">
                <p className={`text-sm font-bold ${errorWarn ? 'text-amber-900' : 'text-gray-900'}`}>
                  {errorCopy[errorKind].title}
                </p>
                <p className={`text-xs mt-0.5 ${errorWarn ? 'text-amber-800' : 'text-gray-600'}`}>
                  {errorCopy[errorKind].body}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {/* Reintentar SOLO donde reintentar puede funcionar. */}
              {errorUx.canRetry && (
                <button
                  type="button"
                  onClick={() => selected && void quote(selected)}
                  disabled={!selected}
                  className="rounded-full px-4 py-2 text-xs font-bold text-white transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                >
                  {t('retry')}
                </button>
              )}
              {errorUx.canChangeAddress && (
                <button
                  type="button"
                  onClick={() => handleText('')}
                  className="rounded-full px-4 py-2 text-xs font-bold text-white transition hover:opacity-95"
                  style={{ backgroundColor: accentColor }}
                >
                  {t('addressChangeAddress')}
                </button>
              )}
              <button
                type="button"
                onClick={onSwitchToPickup}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
              >
                {t('addressSwitchToPickup')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sticky footer */}
      <div
        className="border-t border-gray-100 bg-white px-5 pt-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: accentColor }}
        >
          {canContinue ? (continueLabel ?? `${tCommon('next')} →`) : t('addressCtaNeedAddress')}
        </button>
        <button
          type="button"
          onClick={onSwitchToPickup}
          className="mt-2 w-full py-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition"
        >
          {t('addressSwitchToPickup')}
        </button>
      </div>
    </div>
  )
}
